import type { FeatureCollection, Feature, Polygon, MultiPolygon, Position } from 'geojson'
import { bboxPolygon, booleanDisjoint } from '@turf/turf'
import { GeoTiffBandResultDto, GeoTiffBandInfo } from '../dtos/GeoTiffBandResult.dto'
import {
	GeoTiffProjectionProvider,
	type Bbox,
} from '../../@shared/providers/GeoTiffProjection.provider'

export type GeoImageOverlay = {
	/** PNG em data URI, pronto para um image source do Mapbox. */
	dataUrl: string
	/** Extensao em WGS84: [oeste, sul, leste, norte]. */
	bounds: Bbox
	/**
	 * Faixa de valores do raster, presente so em banda unica (LiDAR, MDT), onde
	 * a cor representa altura ou cota e a legenda precisa dos extremos.
	 */
	valueRange?: { min: number; max: number }
}

/**
 * Rampa de elevacao, do mais baixo ao mais alto. Azul e vermelho nos extremos e
 * a convencao em altimetria, e o contraste sobrevive ao satelite por baixo — o
 * tom de cinza anterior se confundia com o terreno.
 */
const ELEVATION_RAMP: Array<[number, number, number]> = [
	[44, 123, 182], // azul
	[171, 217, 233], // ciano claro
	[255, 255, 191], // amarelo palido
	[253, 174, 97], // laranja
	[215, 25, 28], // vermelho
]

/**
 * Maior lado do PNG gerado. Um GeoTIFF de 10000x10000 ocuparia 400MB so no
 * ImageData e estouraria o limite de area de canvas do navegador, entao a
 * imagem e reamostrada para exibicao.
 */
const MAX_DIMENSION = 2048

/**
 * Converte o GeoTIFF ja extraido em um overlay georreferenciado para o mapa.
 *
 * PONTO DE EXTENSAO: hoje a fonte e o arquivo que o usuario acabou de enviar,
 * que e o que permite validar se a imagem subiu como esperado. Quando o backend
 * expuser o artefato pre-processado, basta trocar quem alimenta o
 * GeoTiffBandResultDto — o resto do fluxo de validacao nao muda.
 */
export class GeoImageOverlayProvider {
	/**
	 * `clipTo` recorta a imagem ao poligono informado — o buffer da fazenda —,
	 * para o overlay nao cobrir o mapa com a moldura retangular do GeoTIFF.
	 */
	static build(
		result: GeoTiffBandResultDto,
		clipTo?: FeatureCollection | Feature | null,
	): GeoImageOverlay | null {
		return this._render(result, clipTo ?? null, (imageData, outWidth, outHeight) =>
			this._paint(imageData, result, outWidth, outHeight),
		)
	}

	/**
	 * Mascara da area efetivamente coberta: pinta so os pixels com dado, na cor
	 * informada, e deixa os noData transparentes. Diferente do retangulo do bbox,
	 * acompanha o recorte real da imagem — util quando o arquivo traz apenas os
	 * talhoes e o resto vem vazio.
	 */
	/**
	 * Devolve null em banda unica: ali a rampa de elevacao ja pinta todo pixel
	 * com dado, e a mascara verde por cima esconderia justamente a variacao de
	 * altura que se quer ver.
	 */
	static buildCoverageMask(
		result: GeoTiffBandResultDto,
		clipTo?: FeatureCollection | Feature | null,
		color: [number, number, number] = [27, 94, 32],
		alpha = 170,
	): GeoImageOverlay | null {
		if (result.bands.length === 1) return null

		return this._render(result, clipTo ?? null, (imageData, outWidth, outHeight) =>
			this._paintMask(imageData, result, outWidth, outHeight, color, alpha),
		)
	}

	private static _render(
		result: GeoTiffBandResultDto,
		clipTo: FeatureCollection | Feature | null,
		painter: (imageData: ImageData, outWidth: number, outHeight: number) => void,
	): GeoImageOverlay | null {
		const { width, height, bbox, epsg, bandCount } = result.metadata

		if (!bbox || bbox.some((value) => !Number.isFinite(value))) {
			console.error('[GeoImageOverlay] TIFF sem bbox válido; imagem não será exibida', { bbox })
			return null
		}

		const bounds = this._toLatLngBounds(bbox, epsg)
		if (!bounds) return null

		if (!result.bands.length) {
			console.error('[GeoImageOverlay] TIFF sem bandas legíveis', { bandCount })
			return null
		}

		const scale = Math.min(1, MAX_DIMENSION / Math.max(width, height))
		const outWidth = Math.max(1, Math.round(width * scale))
		const outHeight = Math.max(1, Math.round(height * scale))

		const canvas = document.createElement('canvas')
		canvas.width = outWidth
		canvas.height = outHeight

		const context = canvas.getContext('2d')
		if (!context) {
			console.error('[GeoImageOverlay] canvas 2D indisponível')
			return null
		}

		const imageData = context.createImageData(outWidth, outHeight)
		painter(imageData, outWidth, outHeight)

		if (clipTo) {
			// putImageData ignora clipping, entao a imagem vai para um canvas
			// intermediario e e desenhada de volta ja recortada.
			const source = document.createElement('canvas')
			source.width = outWidth
			source.height = outHeight
			source.getContext('2d')?.putImageData(imageData, 0, 0)

			const clipped = this._clipPath(context, clipTo, bounds, outWidth, outHeight)
			if (clipped) {
				context.clip()
				context.drawImage(source, 0, 0)
			} else {
				context.putImageData(imageData, 0, 0)
			}
		} else {
			context.putImageData(imageData, 0, 0)
		}

		const bandaUnica = result.bands.length === 1 ? result.bands[0] : null

		return {
			dataUrl: canvas.toDataURL('image/png'),
			bounds,
			...(bandaUnica ? { valueRange: { min: bandaUnica.min, max: bandaUnica.max } } : {}),
		}
	}

	private static _clipPath(
		context: CanvasRenderingContext2D,
		clipTo: FeatureCollection | Feature,
		bounds: Bbox,
		outWidth: number,
		outHeight: number,
	): boolean {
		const [west, south, east, north] = bounds
		const lngSpan = east - west
		const latSpan = north - south
		if (lngSpan <= 0 || latSpan <= 0) return false

		const features = clipTo.type === 'FeatureCollection' ? clipTo.features : [clipTo]
		let hasRing = false

		context.beginPath()

		for (const feature of features) {
			const geometry = feature.geometry as Polygon | MultiPolygon | null
			if (!geometry) continue

			const polygons: Position[][][] =
				geometry.type === 'MultiPolygon'
					? geometry.coordinates
					: geometry.type === 'Polygon'
						? [geometry.coordinates]
						: []

			for (const polygon of polygons) {
				for (const ring of polygon) {
					if (ring.length < 3) continue

					ring.forEach(([lng, lat], index) => {
						const x = ((lng - west) / lngSpan) * outWidth
						const y = ((north - lat) / latSpan) * outHeight
						if (index === 0) context.moveTo(x, y)
						else context.lineTo(x, y)
					})

					context.closePath()
					hasRing = true
				}
			}
		}

		return hasRing
	}

	private static _toLatLngBounds(bbox: Bbox, epsg: number | null): Bbox | null {
		if (epsg === null) {
			// Sem GeoKeys nao da para saber o CRS. Se o bbox ja parece lat/lng,
			// vale tentar; caso contrario nao ha como posicionar a imagem.
			if (GeoTiffProjectionProvider.isPlausibleLatLng(bbox)) return bbox
			console.error(
				'[GeoImageOverlay] TIFF sem EPSG e com bbox fora de lat/lng; imagem não será exibida',
				{ bbox },
			)
			return null
		}

		const reprojected = GeoTiffProjectionProvider.reprojectBboxToLatLng(bbox, epsg)

		if (!reprojected) {
			console.error(
				`[GeoImageOverlay] EPSG ${epsg} não suportado para reprojeção; imagem não será exibida`,
				{ bbox },
			)
			return null
		}

		if (!GeoTiffProjectionProvider.isPlausibleLatLng(reprojected)) {
			console.error('[GeoImageOverlay] bbox reprojetado fora dos limites de lat/lng', {
				epsg,
				bbox,
				reprojected,
			})
			return null
		}

		return reprojected
	}

	/**
	 * Pinta por amostragem do raster de origem, para nao materializar um
	 * ImageData do tamanho da imagem original.
	 *
	 * Com 3 bandas ou mais compoe RGB; com qualquer outra quantidade cai em
	 * tons de cinza da primeira banda. O fallback importa: uma imagem de 2
	 * bandas ficava inteiramente transparente.
	 */
	private static _paint(
		imageData: ImageData,
		result: GeoTiffBandResultDto,
		outWidth: number,
		outHeight: number,
	): void {
		const { width, height } = result.metadata
		const pixels = imageData.data
		const useRgb = result.bands.length >= 3
		const channels = useRgb ? result.bands.slice(0, 3) : [result.bands[0]]

		for (let y = 0; y < outHeight; y++) {
			const sourceY = Math.min(height - 1, Math.floor((y * height) / outHeight))

			for (let x = 0; x < outWidth; x++) {
				const sourceX = Math.min(width - 1, Math.floor((x * width) / outWidth))
				const sourceIndex = sourceY * width + sourceX
				const offset = (y * outWidth + x) * 4

				if (this._isNoData(channels, sourceIndex)) {
					pixels[offset + 3] = 0
					continue
				}

				if (useRgb) {
					pixels[offset] = this._normalize(channels[0], sourceIndex)
					pixels[offset + 1] = this._normalize(channels[1], sourceIndex)
					pixels[offset + 2] = this._normalize(channels[2], sourceIndex)
				} else {
					// Banda unica e altura ou cota: a rampa separa alto de baixo, o que
					// o tom de cinza nao entregava.
					const banda = channels[0]
					const faixa = banda.max - banda.min
					const t = faixa === 0 ? 0 : (banda.data[sourceIndex] - banda.min) / faixa
					const [r, g, b] = this._rampColor(t)
					pixels[offset] = r
					pixels[offset + 1] = g
					pixels[offset + 2] = b
				}

				pixels[offset + 3] = 255
			}
		}
	}

	private static _paintMask(
		imageData: ImageData,
		result: GeoTiffBandResultDto,
		outWidth: number,
		outHeight: number,
		[red, green, blue]: [number, number, number],
		alpha: number,
	): void {
		const { width, height } = result.metadata
		const pixels = imageData.data
		const channels = result.bands.length >= 3 ? result.bands.slice(0, 3) : [result.bands[0]]

		for (let y = 0; y < outHeight; y++) {
			const sourceY = Math.min(height - 1, Math.floor((y * height) / outHeight))

			for (let x = 0; x < outWidth; x++) {
				const sourceX = Math.min(width - 1, Math.floor((x * width) / outWidth))
				const offset = (y * outWidth + x) * 4

				if (this._isNoData(channels, sourceY * width + sourceX)) {
					pixels[offset + 3] = 0
					continue
				}

				pixels[offset] = red
				pixels[offset + 1] = green
				pixels[offset + 2] = blue
				pixels[offset + 3] = alpha
			}
		}
	}

	/**
	 * Sentinelas usadas por GDAL quando o arquivo nao declara GDALNoData. O limite
	 * negativo cobre o -3.4e38 do float32; o -9999 e convencao em raster de
	 * elevacao. Nenhum dos dois ocorre como altura ou reflectancia real.
	 */
	/** Interpola a rampa para t em [0,1]. */
	private static _rampColor(t: number): [number, number, number] {
		const limitado = Math.max(0, Math.min(1, t))
		const escalado = limitado * (ELEVATION_RAMP.length - 1)
		const inferior = Math.floor(escalado)
		const superior = Math.min(ELEVATION_RAMP.length - 1, inferior + 1)
		const fracao = escalado - inferior
		const a = ELEVATION_RAMP[inferior]
		const b = ELEVATION_RAMP[superior]

		return [
			Math.round(a[0] + (b[0] - a[0]) * fracao),
			Math.round(a[1] + (b[1] - a[1]) * fracao),
			Math.round(a[2] + (b[2] - a[2]) * fracao),
		]
	}

	private static _isSentinel(value: number): boolean {
		return value <= -1e30 || value === -9999
	}

	/**
	 * Um pixel e vazio quando qualquer canal traz valor nao finito, igual ao
	 * noData declarado, ou igual a uma sentinela conhecida.
	 *
	 * Sem noData declarado, o zero em TODOS os canais so e tratado como vazio em
	 * imagem multibanda (RGB), onde preto puro e a convencao de entorno de
	 * recorte. Em raster de banda unica o zero e dado legitimo — cota zero num
	 * MDT, ausencia de vegetacao num CHM de LiDAR —, e descarta-lo apagaria area
	 * valida do mapa.
	 */
	private static _isNoData(channels: GeoTiffBandInfo[], index: number): boolean {
		let todosZerados = true

		for (const band of channels) {
			const value = band.data[index]
			if (!Number.isFinite(value)) return true
			if (band.noDataValue !== null && value === band.noDataValue) return true
			if (band.noDataValue === null && this._isSentinel(value)) return true
			if (value !== 0) todosZerados = false
		}

		const multibanda = channels.length >= 3
		const semNoDataDeclarado = channels.every((band) => band.noDataValue === null)
		return multibanda && semNoDataDeclarado && todosZerados
	}

	/**
	 * Confere se a extensao da imagem toca a area informada — o buffer da
	 * fazenda. Uma imagem georreferenciada em outra regiao passa pelo recorte
	 * sem nenhum pixel e nao serve para o processamento daquela fazenda.
	 */
	static intersectsArea(bounds: Bbox, area: FeatureCollection | Feature | null): boolean {
		if (!area) return false
		const features = area.type === 'FeatureCollection' ? area.features : [area]
		const imagem = bboxPolygon(bounds)
		return features.some((feature) => Boolean(feature.geometry) && !booleanDisjoint(imagem, feature as never))
	}

	/** Fracao de pixels com dado, usada no diagnostico da etapa de validacao. */
	static coverageRatio(result: GeoTiffBandResultDto): number {
		const channels = result.bands.length >= 3 ? result.bands.slice(0, 3) : [result.bands[0]]
		if (!channels[0]) return 0

		const total = channels[0].data.length
		if (!total) return 0

		// Amostra: percorrer todos os pixels de uma imagem grande nao paga o custo
		// para um numero que so orienta o usuario.
		const step = Math.max(1, Math.floor(total / 100000))
		let amostrados = 0
		let comDado = 0

		for (let i = 0; i < total; i += step) {
			amostrados++
			if (!this._isNoData(channels, i)) comDado++
		}

		return amostrados ? comDado / amostrados : 0
	}

	private static _normalize(band: GeoTiffBandInfo, index: number): number {
		if (band.max === band.min) return 0
		return ((band.data[index] - band.min) / (band.max - band.min)) * 255
	}

	/**
	 * Mapbox espera os quatro cantos no sentido horario a partir do superior
	 * esquerdo, e nao um bbox.
	 */
	static toImageCoordinates(bounds: Bbox): [number, number][] {
		const [west, south, east, north] = bounds
		return [
			[west, north],
			[east, north],
			[east, south],
			[west, south],
		]
	}
}
